import { useCallback, useEffect, useState } from "react"
import { useSearchParams, useParams } from "react-router-dom"
import { Search } from "lucide-react"
import { PageHeader } from "../../common/ui"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants/navbar"
import { useAppDispatch } from "../../common/hooks"
import { pushToast } from "../../redux/slices/notifications"
import {
  useLookupEnrollmentQuery,
  useRetryEnrollmentMutation,
  useDeleteEnrollmentMutation
} from "../../redux/queries"
import LookupForm from "./components/lookup-form"
import EnrollmentDetail from "./components/enrollment-detail"
import DeleteConfirmationModal from "./components/delete-confirmation-modal"

const STALE_SECONDS = 300
const POLL_INTERVAL_MS = 3000

const EnrollmentLookup = () => {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const { enrollmentId: routeId } = useParams<{ enrollmentId: string }>()

  // Determine lookup params from URL
  const keyParam = searchParams.get("key")
  const idParam = routeId ? parseInt(routeId, 10) : undefined

  // Local state for the active query params
  const [queryParams, setQueryParams] = useState<{
    key?: string
    id?: number
  }>(() => {
    if (idParam) return { id: idParam }
    if (keyParam) return { key: keyParam }
    return {}
  })

  // Sync URL params on mount
  useEffect(() => {
    if (idParam) setQueryParams({ id: idParam })
    else if (keyParam) setQueryParams({ key: keyParam })
  }, [idParam, keyParam])

  const hasParams = !!(queryParams.key || queryParams.id)

  // Determine if we should poll
  const shouldPollFn = (data: any) => {
    if (!data) return false
    if (data.status !== "started") return false
    return Date.now() / 1000 - data.updated_at < STALE_SECONDS
  }

  const {
    data: enrollment,
    error,
    isLoading,
    isFetching
  } = useLookupEnrollmentQuery(queryParams, {
    skip: !hasParams,
    pollingInterval: shouldPollFn(undefined) ? POLL_INTERVAL_MS : 0,
    refetchOnMountOrArgChange: true
  })

  // Manually control polling based on fetched data
  const shouldPoll = enrollment ? shouldPollFn(enrollment) : false

  // Re-query with polling when enrollment status is started and fresh
  const { data: polledEnrollment } = useLookupEnrollmentQuery(queryParams, {
    skip: !hasParams || !shouldPoll,
    pollingInterval: shouldPoll ? POLL_INTERVAL_MS : 0
  })

  const activeEnrollment = polledEnrollment ?? enrollment

  // Mutations
  const [retryEnrollment, { isLoading: isRetrying }] =
    useRetryEnrollmentMutation()
  const [deleteEnrollment, { isLoading: isDeleting }] =
    useDeleteEnrollmentMutation()

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleted, setDeleted] = useState(false)

  const handleLookup = useCallback(
    (params: { key?: string; id?: number }) => {
      setDeleted(false)
      setQueryParams(params)
      // Sync to URL
      if (params.key) {
        setSearchParams({ key: params.key })
      } else if (params.id) {
        setSearchParams({ id: String(params.id) })
      }
    },
    [setSearchParams]
  )

  const handleRetry = useCallback(async () => {
    if (!activeEnrollment) return
    try {
      const result = await retryEnrollment(
        activeEnrollment.enrollment_id
      ).unwrap()
      if (result.status === "complete") {
        dispatch(
          pushToast({
            msg: "Enrollment complete",
            theme: "success",
            timeout: 3000
          })
        )
      } else if (result.status === "partial") {
        dispatch(
          pushToast({
            msg: `Still partial: ${result.message}`,
            theme: "warning",
            timeout: 5000
          })
        )
      }
    } catch (err: any) {
      const status = err?.status
      if (status === 409) {
        dispatch(
          pushToast({
            msg: "Already running — wait for it to finish or stall",
            theme: "info",
            timeout: 4000
          })
        )
      } else if (status === 429) {
        dispatch(
          pushToast({
            msg: "Retry limit reached",
            theme: "danger",
            timeout: 4000
          })
        )
      } else {
        dispatch(
          pushToast({
            msg: err?.data?.message ?? "Retry failed",
            theme: "danger",
            timeout: 4000
          })
        )
      }
    }
  }, [activeEnrollment, retryEnrollment, dispatch])

  const handleDeleteConfirm = useCallback(async () => {
    if (!activeEnrollment) return
    try {
      await deleteEnrollment(activeEnrollment.enrollment_id).unwrap()
      dispatch(
        pushToast({
          msg: "Enrollment deleted successfully",
          theme: "success",
          timeout: 3000
        })
      )
      setShowDeleteModal(false)
      setDeleted(true)
    } catch (err: any) {
      dispatch(
        pushToast({
          msg: err?.data?.message ?? "Delete failed",
          theme: "danger",
          timeout: 4000
        })
      )
    }
  }, [activeEnrollment, deleteEnrollment, dispatch])

  // Error message extraction
  const errorMessage = error
    ? typeof (error as any)?.data === "object" && (error as any)?.data?.message
      ? (error as any).data.message
      : "Enrollment not found"
    : null

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.enroll}>
      <div className="min-h-screen bg-neutral-50/40">
        <div className="max-w-4xl mx-auto p-6">
          <PageHeader
            title={
              <span className="flex items-center gap-3">
                <span>Enrollment Lookup</span>
                <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-primary-600">
                  Admin
                </span>
              </span>
            }
            hasSubtitle
            subtitle="Look up an enrollment attempt by idempotency key or ID to check status, retry, or clean up."
            className="mb-6"
          />

          {/* Lookup form */}
          <div className="rounded-lg border border-neutral-200 bg-white p-5 mb-6">
            <LookupForm
              initialValue={keyParam ?? routeId ?? ""}
              isLoading={isLoading || isFetching}
              onSubmit={handleLookup}
            />
          </div>

          {/* Results */}
          {isLoading && hasParams && (
            <div className="rounded-lg border border-neutral-200 bg-white p-8 text-center">
              <div className="inline-flex items-center gap-2 text-sm text-neutral-500">
                <Search size={16} className="animate-pulse" />
                <span>Looking up enrollment...</span>
              </div>
            </div>
          )}

          {errorMessage && !isLoading && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          {deleted && !isLoading && (
            <div className="rounded-lg border border-neutral-200 bg-white p-8 text-center">
              <p className="text-sm text-neutral-500">
                Enrollment deleted. Enter a new key or ID to look up another.
              </p>
            </div>
          )}

          {activeEnrollment && !isLoading && !deleted && (
            <EnrollmentDetail
              enrollment={activeEnrollment}
              isRetrying={isRetrying}
              onRetry={handleRetry}
              onDelete={() => setShowDeleteModal(true)}
            />
          )}

          {/* Delete confirmation */}
          {activeEnrollment && (
            <DeleteConfirmationModal
              open={showDeleteModal}
              enrollmentId={activeEnrollment.enrollment_id}
              isDeleting={isDeleting}
              onConfirm={handleDeleteConfirm}
              onCancel={() => setShowDeleteModal(false)}
            />
          )}
        </div>
      </div>
    </RequireRole>
  )
}

export default EnrollmentLookup
